import { supabase } from '../lib/supabase';
class UserSettingsService {
    /**
     * Get user settings by user ID
     */
    async getUserSettings(userId) {
        try {
            const { data, error } = await supabase
                .from('user_settings')
                .select('*')
                .eq('user_id', userId)
                .single();
            if (error && error.code !== 'PGRST116') {
                // PGRST116 is "not found" - that's okay
                console.error('Error fetching user settings:', error);
                return null;
            }
            return data;
        }
        catch (error) {
            console.error('Error in getUserSettings:', error);
            return null;
        }
    }
    /**
     * Create or update user settings
     */
    async upsertUserSettings(settings) {
        try {
            const { data, error } = await supabase
                .from('user_settings')
                .upsert({
                user_id: settings.user_id,
                theme: settings.theme,
                default_workspace_id: settings.default_workspace_id,
                sidebar_collapsed: settings.sidebar_collapsed,
                notifications_enabled: settings.notifications_enabled,
                email_notifications: settings.email_notifications,
                default_view: settings.default_view,
                show_completed_tasks: settings.show_completed_tasks,
                preferences: settings.preferences,
                updated_at: new Date().toISOString()
            }, {
                onConflict: 'user_id'
            })
                .select()
                .single();
            if (error) {
                console.error('Error upserting user settings:', error);
                return null;
            }
            return data;
        }
        catch (error) {
            console.error('Error in upsertUserSettings:', error);
            return null;
        }
    }
    /**
     * Update specific preference fields
     */
    async updatePreferences(userId, preferences) {
        try {
            // Get existing settings first
            const existing = await this.getUserSettings(userId);
            const currentPreferences = existing?.preferences || {};
            const { error } = await supabase
                .from('user_settings')
                .upsert({
                user_id: userId,
                preferences: { ...currentPreferences, ...preferences },
                updated_at: new Date().toISOString()
            }, {
                onConflict: 'user_id'
            });
            if (error) {
                console.error('Error updating preferences:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Error in updatePreferences:', error);
            return false;
        }
    }
    /**
     * Update theme setting
     */
    async updateTheme(userId, theme) {
        try {
            const { error } = await supabase
                .from('user_settings')
                .upsert({
                user_id: userId,
                theme,
                updated_at: new Date().toISOString()
            }, {
                onConflict: 'user_id'
            });
            if (error) {
                console.error('Error updating theme:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Error in updateTheme:', error);
            return false;
        }
    }
    /**
     * Update sidebar collapsed state
     */
    async updateSidebarCollapsed(userId, collapsed) {
        try {
            const { error } = await supabase
                .from('user_settings')
                .upsert({
                user_id: userId,
                sidebar_collapsed: collapsed,
                updated_at: new Date().toISOString()
            }, {
                onConflict: 'user_id'
            });
            if (error) {
                console.error('Error updating sidebar collapsed:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Error in updateSidebarCollapsed:', error);
            return false;
        }
    }
    /**
     * Get email credentials from user preferences
     * NOTE: In production, these should be encrypted at rest
     */
    async getEmailCredentials(userId) {
        try {
            const settings = await this.getUserSettings(userId);
            const preferences = settings?.preferences;
            return preferences?.emailCredentials || null;
        }
        catch (error) {
            console.error('Error getting email credentials:', error);
            return null;
        }
    }
    /**
     * Update email credentials in user preferences
     * NOTE: In production, credentials should be encrypted before storage
     * For now, we store them plaintext - Edge Functions will handle encryption
     */
    async updateEmailCredentials(userId, credentials) {
        try {
            // updatePreferences already merges with existing preferences
            const success = await this.updatePreferences(userId, {
                emailCredentials: credentials
            });
            return success;
        }
        catch (error) {
            console.error('Error updating email credentials:', error);
            return false;
        }
    }
    /**
     * Clear email credentials from user preferences
     */
    async clearEmailCredentials(userId) {
        try {
            const existing = await this.getUserSettings(userId);
            const currentPreferences = existing?.preferences || {};
            // Remove emailCredentials from preferences
            const { emailCredentials, ...restPreferences } = currentPreferences;
            const success = await this.updatePreferences(userId, restPreferences);
            return success;
        }
        catch (error) {
            console.error('Error clearing email credentials:', error);
            return false;
        }
    }
}
export const userSettingsService = new UserSettingsService();
